import React, { useState, useCallback } from 'react';
import {
  IndeedThemeProvider,
  Box,
  Heading,
  Text,
  Button,
  Input,
} from '@indeed/ifl-components';
import { NewWindow } from '@indeed/ifl-icons';
import { colors } from '@indeed/ifl-primitives';
import TextStat from './components/textStat';
import AvgSalary from './components/avgSalary';
import SampleDataNotice from './components/sampleDataNotice';
import { i18nDateFormat } from './utils/localizationUtils';
import { Cta, Localization } from './types';
import { getCurrency } from 'locale-currency';

interface InlineHiringInsightsProps {
  displayTitle: string;
  modalData: {
    sections: Record<string, unknown>[];
    ctas: Cta[];
    sourceName: string;
    sourceDate: string;
    modalTitleSuffix: string;
    localization: Localization;
    showSampleNotice?: boolean;
  };
}

const INLINE_SECTIONS = ['job_seekers', 'resumes', 'average_salary'];

const InlineHiringInsights: React.FC<InlineHiringInsightsProps> = ({
  displayTitle,
  modalData: { sourceName, sourceDate, sections, ctas, localization, modalTitleSuffix, showSampleNotice },
}) => {
  const [zipCode, setZipCode] = useState('');

  const inlineSections = sections.filter(
    (s: Record<string, unknown>) =>
      typeof s.name === 'string' && INLINE_SECTIONS.includes(s.name)
  );

  const insightsCta = ctas?.find(
    (c: Cta) => c.show && c.url?.includes('hiring-insights')
  );

  const buildCtaUrl = useCallback(() => {
    if (!insightsCta?.url) {
      return '';
    }
    try {
      const url = new URL(insightsCta.url);
      url.searchParams.set('l', zipCode.trim());
      return url.toString();
    } catch {
      return insightsCta.url;
    }
  }, [insightsCta, zipCode]);

  const handleGoToInsights = useCallback(() => {
    const url = buildCtaUrl();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, [buildCtaUrl]);

  const zipPlaceholder = localization.zipCodePlaceholder || 'Enter a Zip Code...';

  if (!inlineSections.length) {
    return null;
  }

  return (
    <IndeedThemeProvider>
      <Box sx={{ pt: 4 }}>
        {/* eslint-disable-next-line @indeed/uxqual/use-space-theme-values */}
        <Box sx={{ mb: '1.25rem' }}>
          <Heading as="h3" level={5} sx={{ textTransform: 'capitalize', mb: 1 }}>
            {`${displayTitle}: ${modalTitleSuffix}`}
          </Heading>
          <Text level={1} sx={{ color: colors.neutral[600] }}>
            {`${sourceName} - ${i18nDateFormat(sourceDate, localization.locale)}`}
          </Text>
          {showSampleNotice && <SampleDataNotice />}
        </Box>

        <Box
          sx={{
            backgroundColor: 'background',
            // eslint-disable-next-line @indeed/uxqual/use-border-radius-theme-values
            borderRadius: '0.75rem',
            // eslint-disable-next-line @indeed/uxqual/use-space-theme-values
            padding: '0.25rem 1.25rem 1rem',
          }}
        >
          {inlineSections.map((component: any) => (
            <Box
              key={component.name}
              sx={{
                borderBottom: 'solid 1px',
                borderColor: 'border.weak',
                py: 4,
                ':last-of-type': { borderBottom: 0 },
              }}
            >
              {(component.name === 'job_seekers' || component.name === 'resumes') && (
                <TextStat
                  label={component.label}
                  amount={parseInt(component.amount, 10)}
                  change={component.change_amount ? parseInt(component.change_amount, 10) : undefined}
                  date={component.date}
                  description=""
                  info={component.tooltip_info}
                  localization={localization}
                />
              )}
              {component.name === 'average_salary' && (
                <AvgSalary
                  label={component.label}
                  avgRate={parseFloat(component.average)}
                  unit={component.unit}
                  minRate={parseFloat(component.low)}
                  maxRate={parseFloat(component.high)}
                  info={component.tooltip_info}
                  currencyCode={localization.currency || getCurrency(localization.locale)}
                  localization={localization}
                />
              )}
            </Box>
          ))}

          {insightsCta && (
            <Box
              sx={{
                borderTop: 'solid 1px',
                borderColor: 'border.weak',
                pt: 4,
                pb: 4,
              }}
            >
              {localization.marketLabel && (
                <Text sx={{ fontSize: 3, fontWeight: 'bold', mb: 4 }}>
                  {localization.marketLabel}
                </Text>
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: ['column', 'row'],
                  alignItems: ['stretch', 'center'],
                  gap: 3,
                  pt: 4,
                }}
              >
                <Box sx={{ flex: [undefined, 1] }}>
                  <Input
                    value={zipCode}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setZipCode(e.target.value)
                    }
                    placeholder={zipPlaceholder}
                    aria-label={zipPlaceholder}
                    data-tn-element="inline-insights-zipcode"
                  />
                </Box>
                <Button
                  type="button"
                  onClick={handleGoToInsights}
                  iconAfter={<NewWindow size="xs" aria-hidden />}
                  data-tn-element="job-title-modal-cta"
                >
                  {localization.goToInsightsText || insightsCta.text}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </IndeedThemeProvider>
  );
};

export default InlineHiringInsights;
